import { MuscleGroup } from '.';
import { Lookup, isInArray } from './typeUtils';

const _muscleHeatmapKeys = [
  'abdominals',
  'shoulders',
  'chest',
  'lower_back',
  'biceps',
  'triceps',
  'upper_back',
  'quadriceps',
  'calves',
  'lats',
  'hamstrings',
  'glutes',
  'abductors',
  'adductors',
  'traps',
  'forearms',
  'neck',
] as const;

export type MuscleHeatmapKey = Lookup<typeof _muscleHeatmapKeys>;
export const isMuscleHeatmapKey = (x: string): x is MuscleHeatmapKey =>
  isInArray(x, _muscleHeatmapKeys);

export type MuscleHeatmapData = { [key in MuscleHeatmapKey]?: number };

/**
 * Calculates muscle group opacity from exercise data: primary muscle groups are 1.0, secondary are 0.5
 */
export const getMuscleHeatmapDataForExercises = (
  exercises: (
    | {
        muscle_group: MuscleGroup;
        other_muscles: MuscleGroup[];
      }
    | undefined
  )[],
): MuscleHeatmapData => {
  // initialise all keys to 0
  const data: MuscleHeatmapData = _muscleHeatmapKeys.reduce(
    (prev, curr) => ({ ...prev, [curr]: 0 }),
    {},
  );

  for (const e of exercises) {
    if (!e) continue;

    if (isMuscleHeatmapKey(e.muscle_group)) {
      data[e.muscle_group] = 1;
    }

    for (const m of e.other_muscles) {
      if (isMuscleHeatmapKey(m)) {
        data[m] = Math.max(data[m] ?? 0, 0.5);
      }
    }
  }

  return data;
};

/**
 *
 * @param workoutsOrRoutines An array of objects with an array of exercises
 * @param getExerciseTemplateWithId A function that returns an ExerciseTemplate (or undefined) given an ID
 * @returns MuscleHeatmapData with a value that represents how much a muscle group was used in the workouts or routines:
 * A primary muscle counts as 1.0 per workout/routine, and a secondary muscle counts as 0.5 per workout/routine
 */
export const getMuscleHeatmapDataForWorkoutsOrRoutines = ({
  workoutsOrRoutines,
}: {
  workoutsOrRoutines: {
    exercises: {
      muscle_group: MuscleGroup;
      other_muscles: MuscleGroup[];
    }[];
  }[];
}) => {
  // initialise all keys to 0
  const data: MuscleHeatmapData = _muscleHeatmapKeys.reduce(
    (prev, curr) => ({ ...prev, [curr]: 0 }),
    {},
  );

  workoutsOrRoutines.forEach(workoutOrRoutine => {
    const muscleData = getMuscleHeatmapDataForExercises(workoutOrRoutine.exercises);
    Object.keys(muscleData).forEach(muscle => {
      data[muscle as MuscleHeatmapKey] =
        (data[muscle as MuscleHeatmapKey] || 0) + (muscleData[muscle as MuscleHeatmapKey] || 0);
    });
  });

  return data;
};
